/**
 * Movement FSM Constants — States, Events, and Transition Table.
 *
 * Single source of truth for the movement recording flow.
 * Pure data — no side effects, no React dependencies.
 */

// ─── States ────────────────────────────────────────────────────────────
export const MovementState = Object.freeze({
  IDLE: 'IDLE',
  SHOWING_WELCOME_INSTRUCTION: 'SHOWING_WELCOME_INSTRUCTION',
  SHOWING_MOVEMENT_INSTRUCTION: 'SHOWING_MOVEMENT_INSTRUCTION',
  WAITING_FOR_PERSON_IN_BOX: 'WAITING_FOR_PERSON_IN_BOX',
  WAITING_FOR_USER_VALIDATION: 'WAITING_FOR_USER_VALIDATION',
  USER_READY_FOR_PRE_RECORDING: 'USER_READY_FOR_PRE_RECORDING',
  RUNNING_PRE_RECORDING_COUNTDOWN: 'RUNNING_PRE_RECORDING_COUNTDOWN',
  RECORDING_MOVEMENT_VIDEO: 'RECORDING_MOVEMENT_VIDEO',
  RECORDING_INTERRUPTED_BY_MULTIPLE_PERSON: 'RECORDING_INTERRUPTED_BY_MULTIPLE_PERSON',
  MOVEMENT_RECORDING_COMPLETED: 'MOVEMENT_RECORDING_COMPLETED',
});

// ─── Events ────────────────────────────────────────────────────────────
export const MovementEvent = Object.freeze({
  DATA_LOADED: 'DATA_LOADED',
  WELCOME_DISMISSED: 'WELCOME_DISMISSED',
  INSTRUCTION_AUDIO_COMPLETE: 'INSTRUCTION_AUDIO_COMPLETE',
  INSTRUCTION_DISMISSED: 'INSTRUCTION_DISMISSED',
  PERSON_DETECTED_IN_BOX: 'PERSON_DETECTED_IN_BOX',
  REQUIREMENTS_MET: 'REQUIREMENTS_MET',
  REQUIREMENTS_LOST: 'REQUIREMENTS_LOST',
  PRE_RECORDING_AUDIO_DONE: 'PRE_RECORDING_AUDIO_DONE',
  COUNTDOWN_COMPLETE: 'COUNTDOWN_COMPLETE',
  COUNTDOWN_CANCELLED: 'COUNTDOWN_CANCELLED',
  RECORDING_DURATION_COMPLETE: 'RECORDING_DURATION_COMPLETE',
  MULTI_PERSON_DETECTED: 'MULTI_PERSON_DETECTED',
  USER_LEFT_FRAME: 'USER_LEFT_FRAME',
  MOVEMENT_RESET: 'MOVEMENT_RESET',
  EXERCISE_SELECTED: 'EXERCISE_SELECTED',
  EXERCISE_COMPLETED: 'EXERCISE_COMPLETED',
});

// ─── Transition Table ──────────────────────────────────────────────────
// Key: current state → { event: { to, guard? } | [ { to, guard? } ] }
// Array form = guarded transitions (first passing guard wins).

export const TRANSITIONS = Object.freeze({
  [MovementState.IDLE]: {
    [MovementEvent.DATA_LOADED]: [
      { to: MovementState.SHOWING_WELCOME_INSTRUCTION, guard: (ctx) => !ctx.hasSeenInstructions || ctx.isExamineticsEnabled },
      { to: MovementState.SHOWING_MOVEMENT_INSTRUCTION, guard: (ctx) => ctx.hasSeenInstructions && !ctx.isExamineticsEnabled },
    ],
  },

  [MovementState.SHOWING_WELCOME_INSTRUCTION]: {
    [MovementEvent.WELCOME_DISMISSED]: {
      to: MovementState.SHOWING_MOVEMENT_INSTRUCTION,
    },
  },

  [MovementState.SHOWING_MOVEMENT_INSTRUCTION]: {
    [MovementEvent.INSTRUCTION_AUDIO_COMPLETE]: {
      to: MovementState.WAITING_FOR_USER_VALIDATION,
      guard: (ctx) => ctx.isExamineticsEnabled,
    },
    [MovementEvent.INSTRUCTION_DISMISSED]: {
      to: MovementState.WAITING_FOR_USER_VALIDATION,
      guard: (ctx) => ctx.isExamineticsEnabled,
    },
    [MovementEvent.PERSON_DETECTED_IN_BOX]: {
      to: MovementState.WAITING_FOR_PERSON_IN_BOX,
      guard: (ctx) => !ctx.isExamineticsEnabled,
    },
    [MovementEvent.EXERCISE_SELECTED]: {
      to: MovementState.SHOWING_MOVEMENT_INSTRUCTION,
    },
  },

  [MovementState.WAITING_FOR_PERSON_IN_BOX]: {
    [MovementEvent.INSTRUCTION_AUDIO_COMPLETE]: {
      to: MovementState.WAITING_FOR_USER_VALIDATION,
    },
    [MovementEvent.EXERCISE_SELECTED]: {
      to: MovementState.SHOWING_MOVEMENT_INSTRUCTION,
    },
  },

  [MovementState.WAITING_FOR_USER_VALIDATION]: {
    [MovementEvent.REQUIREMENTS_MET]: {
      to: MovementState.USER_READY_FOR_PRE_RECORDING,
      guard: (ctx) => !ctx.isAnyModalOpen,
    },
    [MovementEvent.EXERCISE_SELECTED]: {
      to: MovementState.SHOWING_MOVEMENT_INSTRUCTION,
    },
  },

  [MovementState.USER_READY_FOR_PRE_RECORDING]: {
    [MovementEvent.PRE_RECORDING_AUDIO_DONE]: {
      to: MovementState.RUNNING_PRE_RECORDING_COUNTDOWN,
    },
    [MovementEvent.REQUIREMENTS_LOST]: {
      to: MovementState.WAITING_FOR_USER_VALIDATION,
    },
    [MovementEvent.EXERCISE_SELECTED]: {
      to: MovementState.SHOWING_MOVEMENT_INSTRUCTION,
    },
  },

  [MovementState.RUNNING_PRE_RECORDING_COUNTDOWN]: {
    [MovementEvent.COUNTDOWN_COMPLETE]: {
      to: MovementState.RECORDING_MOVEMENT_VIDEO,
    },
    [MovementEvent.COUNTDOWN_CANCELLED]: {
      to: MovementState.WAITING_FOR_USER_VALIDATION,
    },
    [MovementEvent.REQUIREMENTS_LOST]: {
      to: MovementState.WAITING_FOR_USER_VALIDATION,
    },
    [MovementEvent.EXERCISE_SELECTED]: {
      to: MovementState.SHOWING_MOVEMENT_INSTRUCTION,
    },
  },

  [MovementState.RECORDING_MOVEMENT_VIDEO]: {
    [MovementEvent.RECORDING_DURATION_COMPLETE]: {
      to: MovementState.MOVEMENT_RECORDING_COMPLETED,
    },
    [MovementEvent.MULTI_PERSON_DETECTED]: {
      to: MovementState.RECORDING_INTERRUPTED_BY_MULTIPLE_PERSON,
    },
    [MovementEvent.USER_LEFT_FRAME]: {
      to: MovementState.MOVEMENT_RECORDING_COMPLETED,
    },
    [MovementEvent.EXERCISE_SELECTED]: {
      to: MovementState.SHOWING_MOVEMENT_INSTRUCTION,
    },
  },

  [MovementState.RECORDING_INTERRUPTED_BY_MULTIPLE_PERSON]: {
    [MovementEvent.MOVEMENT_RESET]: {
      to: MovementState.WAITING_FOR_USER_VALIDATION,
    },
    [MovementEvent.EXERCISE_SELECTED]: {
      to: MovementState.SHOWING_MOVEMENT_INSTRUCTION,
    },
  },

  [MovementState.MOVEMENT_RECORDING_COMPLETED]: {
    [MovementEvent.EXERCISE_COMPLETED]: [
      { to: MovementState.SHOWING_MOVEMENT_INSTRUCTION, guard: (ctx) => ctx.hasNextExercise },
    ],
    [MovementEvent.EXERCISE_SELECTED]: {
      to: MovementState.SHOWING_MOVEMENT_INSTRUCTION,
    },
  },
});
